import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Title,
  Group,
  Text,
  Divider,
  SimpleGrid,
  RingProgress,
  Stack,
  Badge,
  Button,
  Progress,
  Box
} from '@mantine/core';
import {
  IconDatabase,
  IconClock,
  IconRefresh,
  IconChartPie
} from '@tabler/icons-react';
import { useCacheControl } from '../../hooks/use-cache-control';
import { CacheMetrics } from '../../types/cache-types';

export function CacheMetricsPanel() {
  const { getCacheMetrics, isAdmin } = useCacheControl();
  const [metrics, setMetrics] = useState<CacheMetrics>({
    totalQueries: 0,
    activeQueries: 0,
    staleQueries: 0,
    queryTypes: {},
    recentlyUpdated: 0
  });
  const [refreshCount, setRefreshCount] = useState(0);

  // Aggiorna le metriche periodicamente
  const memoizedGetCacheMetrics = useCallback(() => {
    return getCacheMetrics();
  }, []); // Dipendenza vuota per eseguirlo solo una volta

  useEffect(() => {
    const updateMetrics = () => {
      const currentMetrics = memoizedGetCacheMetrics();
      setMetrics(currentMetrics);
    };

    // Aggiorna le metriche immediatamente
    updateMetrics();

    // Imposta un intervallo per aggiornare le metriche ogni 5 secondi
    const intervalId = setInterval(updateMetrics, 5000);

    return () => clearInterval(intervalId);
  }, [memoizedGetCacheMetrics, refreshCount]);

  if (!isAdmin) {
    return (
      <Card withBorder shadow="xs" p="lg">
        <Text color="dimmed">
          Accesso riservato agli amministratori.
        </Text>
      </Card>
    );
  }

  const handleManualRefresh = () => {
    setRefreshCount(prev => prev + 1);
  };

  // Calcola le percentuali per le progress bar
  const stalePercentage = metrics.totalQueries
    ? (metrics.staleQueries / metrics.totalQueries) * 100
    : 0;

  const activePercentage = metrics.totalQueries
    ? (metrics.activeQueries / metrics.totalQueries) * 100
    : 0;

  const recentlyUpdatedPercentage = metrics.totalQueries
    ? (metrics.recentlyUpdated / metrics.totalQueries) * 100
    : 0;

  // Colori per vari stati
  const getStaleColor = (percentage: number) => {
    if (percentage < 20) return 'green';
    if (percentage < 50) return 'yellow';
    return 'red';
  };

  return (
    <Card withBorder shadow="sm" p="md">
      <Group justify="space-between" mb="md">
        <Title order={4}>Metriche Cache</Title>
        <Button
          variant="subtle"
          size="xs"
          onClick={handleManualRefresh}
        >
          Aggiorna
        </Button>
      </Group>

      <Text size="sm" color="dimmed" mb="lg">
        Visualizza lo stato attuale della cache e il consumo di memoria.
      </Text>

      <SimpleGrid cols={{ base: 1, sm: 3 }} mb="lg">
        <Card withBorder p="md">
          <Group wrap="nowrap">
            <RingProgress
              size={80}
              roundCaps
              thickness={8}
              sections={[
                { value: activePercentage, color: 'blue' },
              ]}
              label={
                <Box
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                  }}
                >
                  <IconDatabase size={22} />
                </Box>
              }
            />
            <Stack gap={0}>
              <Text fw={700} size="xl">{metrics.totalQueries}</Text>
              <Text size="xs" color="dimmed">Query in cache</Text>
              <Group mt="xs" gap={6}>
                <Badge size="xs" color="blue">{metrics.activeQueries} attive</Badge>
              </Group>
            </Stack>
          </Group>
        </Card>

        <Card withBorder p="md">
          <Group wrap="nowrap">
            <RingProgress
              size={80}
              roundCaps
              thickness={8}
              sections={[
                { value: stalePercentage, color: getStaleColor(stalePercentage) },
              ]}
              label={
                <Box
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                  }}
                >
                  <IconClock size={22} />
                </Box>
              }
            />
            <Stack gap={0}>
              <Text fw={700} size="xl">{metrics.staleQueries}</Text>
              <Text size="xs" color="dimmed">Query obsolete</Text>
              <Text size="xs" color="dimmed">
                {stalePercentage.toFixed(1)}% del totale
              </Text>
            </Stack>
          </Group>
        </Card>

        <Card withBorder p="md">
          <Group wrap="nowrap">
            <RingProgress
              size={80}
              roundCaps
              thickness={8}
              sections={[
                { value: recentlyUpdatedPercentage, color: 'teal' },
              ]}
              label={
                <Box
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                  }}
                >
                  <IconRefresh size={22} />
                </Box>
              }
            />
            <Stack gap={0}>
              <Text fw={700} size="xl">{metrics.recentlyUpdated}</Text>
              <Text size="xs" color="dimmed">Aggiornate di recente</Text>
              <Text size="xs" color="dimmed">
                Ultimi 5 minuti
              </Text>
            </Stack>
          </Group>
        </Card>
      </SimpleGrid>

      <Divider
        my="md"
        label={
          <Group gap={4}>
            <IconChartPie size={14} />
            <Text size="sm">Distribuzione per Tipo</Text>
          </Group>
        }
        labelPosition="center"
      />

      <Stack gap={4}>
        {Object.entries(metrics.queryTypes).map(([type, count]) => (
          <Box key={type}>
            <Group justify="space-between" mb={4}>
              <Text size="sm">{type}</Text>
              <Text size="sm" fw={500}>{count}</Text>
            </Group>
            <Progress
              value={(count / metrics.totalQueries) * 100}
              color={type === 'products' ? 'blue' : type === 'users' ? 'grape' : type === 'orders' ? 'orange' : 'teal'}
              size="sm"
              radius="xl"
            />
          </Box>
        ))}
      </Stack>

      <Text size="xs" color="dimmed" mt="md" ta="center">
        Le metriche vengono aggiornate automaticamente ogni 5 secondi
      </Text>
    </Card>
  );
}